import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, BarChart3, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";

import CarregandoRelatorio from "../../relatorios-ofertas/comum/Carregando";

import { CarregandoBloco, CarregandoFormulario } from "../../../components/carregando";
import { Button } from "../../../components/ui/button";
import { Skeleton } from "../../../components/ui/skeleton";

import { carregarConfig, salvarConfig } from "./api";
import { ROTA_CAMPANHAS, ROTA_RELATORIO } from "./rotas";
import SecaoProdutos from "./SecaoProdutos";
import SecaoRegras from "./SecaoRegras";

const ABAS = [{ id: "regras", rotulo: "Regras" }, { id: "produtos", rotulo: "Produtos" }];

// Página de configuração da Campanha Inteligente (Campanhas → Configurar).
export default function ConfigCampanhaInteligentePage() {
  const navigate = useNavigate();
  const [dados, setDados] = useState(null);
  const [config, setConfig] = useState(null);
  const [ativa, setAtiva] = useState(false);
  const [aba, setAba] = useState("regras");
  const [salvando, setSalvando] = useState(false);
  const [alterado, setAlterado] = useState(false);
  // Erro ao ler a configuração: aviso com "Tentar de novo" em vez de piscar para sempre (25/10/2026)
  const [erro, setErro] = useState(false);
  const [tentativa, setTentativa] = useState(0);

  useEffect(() => {
    setErro(false);
    carregarConfig()
      .then((d) => { setDados(d); setConfig(d.config); setAtiva(!!d.is_active); })
      .catch(() => { setErro(true); toast.error("Não foi possível carregar a configuração"); });
  }, [tentativa]);

  const mudarConfig = (novo) => { setConfig(novo); setAlterado(true); };
  const mudarAtiva = (v) => { setAtiva(v); setAlterado(true); };

  const participando = useMemo(() => {
    if (!config) return 0;
    const produtos = Object.values(config.produtos || {}).filter((r) => r?.participa).length;
    const pizzas = Object.values(config.pizza || {}).reduce((s, t) => s + Object.values(t || {}).filter((r) => r?.participa).length, 0);
    return produtos + pizzas;
  }, [config]);

  const salvar = async () => {
    setSalvando(true);
    try {
      const novo = await salvarConfig({ is_active: ativa, config });
      setDados(novo); setConfig(novo.config); setAtiva(!!novo.is_active); setAlterado(false);
      toast.success(ativa ? "Campanha Inteligente salva e ligada" : "Campanha Inteligente salva");
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Erro ao salvar");
    } finally {
      setSalvando(false);
    }
  };

  return (
    <div className="p-4 sm:p-6">
      <div className="max-w-5xl mx-auto pb-6" data-e2e="config-campanha-inteligente">
        <button type="button" onClick={() => navigate(ROTA_CAMPANHAS)}
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-3">
          <ArrowLeft className="w-4 h-4" /> Campanhas
        </button>
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-fuchsia-600 flex items-center justify-center shadow-lg shadow-purple-500/30">
              <Sparkles className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold flex items-center gap-2">
                Campanha Inteligente
                {dados && (
                  <span className={`text-[11px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full ${ativa ? "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400" : "bg-muted text-muted-foreground"}`}>
                    {ativa ? "Ligada" : "Desligada"}
                  </span>
                )}
              </h1>
              <p className="text-sm text-muted-foreground">Ofertas sorteadas pra cada cliente, dentro do limite que você define.</p>
            </div>
          </div>
          <Button variant="outline" onClick={() => navigate(ROTA_RELATORIO)}>
            <BarChart3 className="w-4 h-4 mr-1.5" /> Ver relatório
          </Button>
        </div>

        {!config && erro ? <CarregandoRelatorio erro onTentar={() => setTentativa((t) => t + 1)} /> : !config ? (
          // Carregamento com cards (25/10/2026): abas, cartão liga/desliga e as regras piscando.
          <div className="space-y-4" aria-busy="true" aria-label="Carregando">
            <div className="flex gap-3 border-b pb-2"><Skeleton className="h-6 w-20" /><Skeleton className="h-6 w-28" /></div>
            <CarregandoBloco altura="h-[134px]" />
            <CarregandoFormulario campos={4} />
          </div>
        ) : (
          <>
            <div className="flex gap-1 border-b mb-4">
              {ABAS.map((a) => (
                <button key={a.id} type="button" onClick={() => setAba(a.id)}
                  className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px transition-colors ${aba === a.id ? "border-purple-500 text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
                  {a.rotulo}{a.id === "produtos" ? ` (${participando})` : ""}
                </button>
              ))}
            </div>
            {aba === "regras"
              ? <SecaoRegras ativa={ativa} onAtiva={mudarAtiva} config={config} onMudarConfig={mudarConfig}
                  produtos={dados.produtos} participando={participando} onIrParaProdutos={() => setAba("produtos")} />
              : <SecaoProdutos produtos={dados.produtos} categorias={dados.categorias} config={config} configSalva={dados.config} onMudarConfig={mudarConfig} />}
          </>
        )}
      </div>

      {config && (
        // Barra presa no pé da área de conteúdo (sticky), não da janela: com "fixed" o
        // menu lateral cobria o começo dela ("Alterações não salvas" sumia).
        <div className="sticky bottom-0 z-20 -mx-4 sm:-mx-6 -mb-4 sm:-mb-6 border-t bg-card/95 backdrop-blur">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
            <p className="text-xs text-muted-foreground">
              {alterado ? <b className="text-amber-600 dark:text-amber-400">Alterações não salvas · </b> : null}
              {participando === 0 ? "Nenhum produto participando ainda." : `${participando} ${participando === 1 ? "item participando" : "itens participando"} do sorteio.`}
            </p>
            <Button onClick={salvar} disabled={salvando} data-e2e="salvar-ofertas">
              {salvando && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />} Salvar
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
